import { useState, useEffect, useRef, useCallback } from 'react';

type AnimationType = 'fade-in' | 'fade-up' | 'scale-in' | 'blur-in';

interface ScrollRevealProps {
  children: React.ReactNode;
  animation?: AnimationType;
  delay?: number;
  duration?: number;
  threshold?: number;
  className?: string;
  once?: boolean;
}

// Envuelve contenido y le aplica una animación de entrada (definida en index.css como @keyframes animate-*) cuando entra al viewport.
export default function ScrollReveal({
  children,
  animation = 'fade-up',
  delay = 0,
  duration = 700,
  threshold = 0.12,
  className = '',
  once = true,
}: ScrollRevealProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const timer = setTimeout(() => setIsVisible(true), delay);
          if (once) observer.unobserve(element);
          return () => clearTimeout(timer);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, delay, once]);

  const animationClass = isVisible ? `animate-${animation}` : 'scroll-reveal';

  return (
    <div
      ref={ref}
      className={`${animationClass} ${className}`}
      style={{
        animationDuration: `${duration}ms`,
        animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        animationDelay: isVisible ? `${delay}ms` : '0ms',
      }}
    >
      {children}
    </div>
  );
}